import { useState } from 'react'
import Image from 'next/image'
import clsx from 'clsx'

/* blocks */
import Link from '@/atoms/Link/Link'
import Carousel from '@/atoms/Carousel/Carousel'

/* utils */
import { IndividualEventsPageAttributes } from '@/utils/queries/events-page'

/* assets */
import { ReactComponent as ArrowRight } from 'public/assets/icons/chevron-right.svg'

interface Props {
  title?: string
  lists: IndividualEventsPageAttributes[]
  isEventSubtypeHidden?: boolean
  showViewMore: boolean
}

function UpcomingEvents({
  title,
  lists,
  isEventSubtypeHidden = false,
  showViewMore
}: Props) {
  const [type, setType] = useState('All')

  const filteredEvents =
    type === 'All' ? lists : lists.filter(data => data.eventSubtype === type)

  const handleSubTypeClick = (value: string) => {
    setType(value)
  }

  return (
    <section className="font-body">
      <div className="container | py-9 md:py-20">
        <div className="flex justify-between">
          {title ? (
            <h4 className="font-heading | text-3xl lg:text-4xl font-semibold text-black text-center md:text-left">
              {title}
            </h4>
          ) : null}
          {showViewMore ? (
            <Link
              href="/events/all-events/"
              className="font-medium text-blue-web-60 | hidden md:block px-4 py-2"
            >
              <div className="flex justify-center items-center">
                View More
                <ArrowRight className="ml-2" />
              </div>
            </Link>
          ) : null}
        </div>
        <nav className={clsx(isEventSubtypeHidden ? 'hidden' : 'block')}>
          <div className="flex gap-5 md:gap-8 items-center | text-base text-white font-medium | py-7">
            {eventsSubTypeList.map(eventSubtype => (
              <div key={eventSubtype.title}>
                <button
                  onClick={() => handleSubTypeClick(eventSubtype.value)}
                  className={clsx(
                    'text-xs md:text-lg hover:text-white hover:bg-blue-web-40 | rounded-full px-2 py-1 md:px-3 md:py-2',
                    type === eventSubtype.value
                      ? 'text-white font-semibold bg-blue-web-40'
                      : 'text-blue-web-90 bg-gray-20'
                  )}
                >
                  {eventSubtype.title}
                </button>
              </div>
            ))}
          </div>
        </nav>
        {filteredEvents.length > 0 ? (
          <Carousel
            className={`mx-auto | overflow-hidden ${
              showViewMore ? 'mb-4 md:mb-0' : ''
            }`}
            autoScroll={true}
            indicatorSize="sm"
            slides={filteredEvents.map(event => (
              <Link key={event.eventTitle} href={`/events/${event.slug}/`}>
                <Image
                  src={event.heroImage?.data?.attributes?.url}
                  alt={event.heroImage?.data?.attributes?.alternativeText ?? ''}
                  width={event.heroImage?.data?.attributes?.width}
                  height={event.heroImage?.data?.attributes?.height}
                  className="border border-blue-web-60 rounded-2xl"
                />
              </Link>
            ))}
          />
        ) : (
          <div className="text-lg font-semibold text-center">
            Sorry, no events to display
          </div>
        )}
        {showViewMore ? (
          <div className="flex justify-center md:hidden">
            <Link
              href="/events/"
              className="flex justify-center items-center | font-medium text-blue-web-60 | border border-blue-web-60 rounded px-4 py-2"
            >
              View More
              <ArrowRight className="ml-2" />
            </Link>
          </div>
        ) : null}
      </div>
    </section>
  )
}

const eventsSubTypeList = [
  {
    title: 'All Events',
    value: 'All'
  },
  {
    title: 'Virtual',
    value: 'Virtual'
  },
  {
    title: 'In Person',
    value: 'In_Person'
  }
]

export default UpcomingEvents
